import type { Allergen } from '../../shared/menu';

const NAMES: Record<Allergen, string> = {
  nuts: 'tree nuts',
  peanut: 'peanuts',
  shellfish: 'shellfish',
  fish: 'fish',
  egg: 'egg',
  soy: 'soy',
  wheat: 'gluten',
  sesame: 'sesame',
  dairy: 'dairy',
  pork: 'pork',
  alcohol: 'alcohol',
};

// Erudus allergen icon font — private-use unicode codepoints map to the
// food-industry-standard EU allergen pictograms (MIT licensed). See
// public/fonts/erudus.woff and the @font-face declaration in styles.css.
// Pork and alcohol aren't EU-regulated allergens, so Erudus doesn't ship
// them — those fall back to inline SVG keyed off the same Lucide aesthetic.
const GLYPHS: Partial<Record<Allergen, string>> = {
  nuts: '\ue90f',
  peanut: '\ue910',
  shellfish: '\ue903',
  fish: '\ue90a',
  egg: '\ue90d',
  soy: '\ue913',
  wheat: '\ue904',
  sesame: '\ue908',
  dairy: '\ue90e',
};

interface ChipProps {
  allergen: Allergen;
  variant: 'confirmed' | 'maybe';
}

function FallbackIcon({ allergen }: { allergen: Allergen }) {
  const common = {
    width: 16,
    height: 16,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  if (allergen === 'pork') {
    // Pig snout (universal food-label symbol for pork)
    return (
      <svg {...common}>
        <ellipse cx="12" cy="12" rx="8" ry="6" />
        <circle cx="9.5" cy="12" r="0.9" fill="currentColor" />
        <circle cx="14.5" cy="12" r="0.9" fill="currentColor" />
      </svg>
    );
  }
  // alcohol: wine glass
  return (
    <svg {...common}>
      <path d="M7 4h10" />
      <path d="M7 4l1 6a4 4 0 0 0 8 0l1-6" />
      <path d="M12 14v6" />
      <path d="M8 20h8" />
    </svg>
  );
}

export function AllergenChip({ allergen, variant }: ChipProps) {
  const isConfirmed = variant === 'confirmed';
  const name = NAMES[allergen];
  const fullLabel = isConfirmed ? `Contains ${name}` : `May contain ${name}`;
  const glyph = GLYPHS[allergen];

  return (
    <span
      role="img"
      aria-label={fullLabel}
      title={fullLabel}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: isConfirmed ? 'var(--color-danger)' : 'transparent',
        color: isConfirmed ? '#fff' : 'var(--color-warning)',
        border: isConfirmed ? 'none' : '1.5px solid var(--color-warning)',
        flexShrink: 0,
      }}
    >
      {glyph ? (
        <span
          aria-hidden
          style={{
            fontFamily: "'erudus', sans-serif",
            fontStyle: 'normal',
            fontWeight: 'normal',
            fontVariant: 'normal',
            textTransform: 'none',
            lineHeight: 1,
            fontSize: 18,
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          }}
        >
          {glyph}
        </span>
      ) : (
        <FallbackIcon allergen={allergen} />
      )}
    </span>
  );
}
